import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, LoaderCircle, Mail, MessageSquare, Trash2, Zap } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { SearchInput } from '@/_shared/components/SearchInput';
import { Badge } from '@/_shared/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/_shared/components/ui/card';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { formatDate } from '@/_shared/lib/format';

function statusVariant(status: string): 'default' | 'secondary' | 'outline' {
  if (status === 'active') return 'default';
  if (status === 'paused') return 'outline';
  return 'secondary'; // draft
}

export function Workflows(): React.ReactElement {
  usePageTitle('Workflows');
  const { wp } = useWorkspace();
  const orpc = useOrpc();
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery(orpc.workflow.list.queryOptions({ input: {} }));

  const deleteMutation = useMutation(
    orpc.workflow.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.workflow.list.queryOptions({ input: {} }).queryKey,
        });
      },
    }),
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      deleteMutation.mutate({ id });
    },
    [deleteMutation],
  );

  const allWorkflows = useMemo(() => data?.workflows ?? [], [data?.workflows]);

  const workflows = useMemo(() => {
    if (!search.trim()) return allWorkflows;
    const q = search.toLowerCase();
    return allWorkflows.filter(
      (w) => w.name.toLowerCase().includes(q) || (w.description?.toLowerCase().includes(q) ?? false),
    );
  }, [allWorkflows, search]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="mb-2 flex items-center justify-between gap-4">
          <h1 className="text-lg font-semibold text-foreground">Workflows</h1>
          {allWorkflows.length > 0 && (
            <SearchInput value={search} onChange={setSearch} placeholder="Search workflows..." className="max-w-xs" />
          )}
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Automated data quality workflows triggered by your conversations.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <CircleAlert size={32} className="mb-4 text-red-500" />
            <h2 className="text-base font-medium text-foreground">Failed to load workflows</h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {error.message || 'An unexpected error occurred. Please try again.'}
            </p>
          </div>
        )}

        {!isLoading && !error && workflows.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Zap size={48} className="mb-4 text-muted-foreground/30" />
            <h2 className="text-base font-medium text-foreground">No workflows yet</h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              Create one by starting a chat and asking to set up an automated workflow.
            </p>
          </div>
        )}

        {!isLoading && !error && workflows.length > 0 && (
          <div className="mx-auto grid max-w-4xl grid-cols-1 gap-4 px-6 py-6 sm:grid-cols-2">
            {workflows.map((workflow) => (
              <Link
                key={workflow.id}
                to={wp(`/workflows/${workflow.id}`)}
                className="group block transition-opacity hover:opacity-80"
              >
                <Card className="h-full">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-sm">{workflow.name}</CardTitle>
                      <div className="flex shrink-0 items-center gap-1">
                        <Badge variant={statusVariant(workflow.status)} className="text-[0.625rem]">
                          {workflow.status}
                        </Badge>
                        {'actionMode' in workflow && workflow.actionMode && (
                          <Badge variant="outline" className="text-[0.625rem]">
                            {workflow.actionMode === 'per_row' ? 'Customer-facing' : 'Internal'}
                          </Badge>
                        )}
                        <button
                          onClick={(e) => handleDelete(e, workflow.id)}
                          className="rounded p-1 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive"
                          aria-label={`Delete ${workflow.name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    {workflow.description && (
                      <CardDescription className="line-clamp-2 text-xs">{workflow.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        {workflow.actionType === 'slack_webhook' ? <MessageSquare size={12} /> : <Mail size={12} />}
                        <span>{workflow.actionType === 'slack_webhook' ? 'Slack' : 'Email'}</span>
                      </div>
                      <span>{formatDate(workflow.updatedAt)}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
